import React from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
    ClipboardList, Building2, BarChart3, FileText, BadgeDollarSign,
    ArrowRightLeft, Plane, Map as MapIcon, TrendingUp, Lock
} from 'lucide-react';

const FinancialCenterLanding = () => {
    const navigate = useNavigate();

    const tools = [
        { label: 'Calculadora Hipotecaria', icon: ClipboardList, desc: 'Estima cuotas mensuales' },
        { label: 'Aranceles y Registros', icon: Building2, desc: 'Costos de CNR y escrituración' },
        { label: 'Comparador de Tasas', icon: BarChart3, desc: 'Analiza ofertas bancarias' },
        { label: 'Guía de Trámites', icon: FileText, desc: 'Paso a paso para comprar' },
        { label: 'Estimador de Valor', icon: BadgeDollarSign, desc: 'Avalúos referenciales' },
        { label: 'Mapa Catastral', icon: MapIcon, desc: 'Ubicación y zonas' },
        { label: 'Créditos H. Lejanos', icon: Plane, desc: 'Para salvadoreños en el exterior' },
        { label: 'Conversor de Unidades', icon: ArrowRightLeft, desc: 'Varas, metros y manzanas' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-24 pb-16">
            <div className="container mx-auto px-4">
                {/* Hero Section */}
                <div className="text-center max-w-4xl mx-auto mb-16 animate-in fade-in zoom-in duration-700">
                    <h1 className="text-4xl md:text-6xl font-extrabold text-[#1A3D5C] mb-6 leading-tight">
                        Toma decisiones financieras <br />
                        <span className="text-blue-600">inteligentes e informadas</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Accede al set de herramientas inmobiliarias más completo de El Salvador.
                        Calculadoras, mapas, guías y más, en un solo lugar.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button
                            size="lg"
                            onClick={() => navigate('/register?redirect=centro-financiero')}
                            className="w-full sm:w-auto px-8 py-6 text-lg font-bold bg-[#1A3D5C] hover:bg-[#15324d] shadow-lg hover:shadow-xl transition-all"
                        >
                            Crear cuenta gratis – Prueba 3 días
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            onClick={() => navigate('/login?redirect=centro-financiero')}
                            className="w-full sm:w-auto px-8 py-6 text-lg font-medium border-slate-300 hover:bg-slate-50 text-slate-700"
                        >
                            Iniciar sesión
                        </Button>
                    </div>
                    <p className="mt-4 text-sm text-gray-500">
                        ¿Ya tienes cuenta? Ingresa para continuar donde lo dejaste.
                    </p>
                </div>

                {/* Preview Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto opacity-80 hover:opacity-100 transition-opacity">
                    {tools.map((tool, idx) => (
                        <div
                            key={idx}
                            onClick={() => navigate('/register?redirect=centro-financiero')}
                            className="flex flex-col items-center justify-center p-6 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group"
                        >
                            <div className="p-4 rounded-full bg-slate-50 group-hover:bg-blue-50 mb-4 transition-colors relative">
                                <tool.icon className="w-8 h-8 text-[#1A3D5C] group-hover:text-blue-600" />
                                <div className="absolute top-0 right-0 bg-amber-400 rounded-full p-1 border-2 border-white shadow-sm">
                                    <Lock className="w-3 h-3 text-white" />
                                </div>
                            </div>
                            <h3 className="font-bold text-gray-800 text-center mb-1">{tool.label}</h3>
                            <p className="text-xs text-gray-500 text-center">{tool.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Trust Indicators */}
                <div className="mt-20 text-center border-t border-slate-200 pt-10">
                    <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-6">
                        CONFIADO POR AGENTES Y DESARROLLADORES EN TODO EL PAÍS
                    </p>
                    <div className="flex justify-center items-center gap-8 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                        {/* Placeholder logos or icons */}
                        <div className="flex items-center gap-2 font-bold text-slate-600"><Building2 className="w-5 h-5" /> InmoCapital</div>
                        <div className="flex items-center gap-2 font-bold text-slate-600"><BadgeDollarSign className="w-5 h-5" /> BancaPro</div>
                        <div className="flex items-center gap-2 font-bold text-slate-600"><MapIcon className="w-5 h-5" /> GeoData</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinancialCenterLanding;
